import { useEffect, useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { diaryManagementService } from '../../services/diaryManagementService';
import { meetingsService } from '../../services/meetingsService';
import { diaryManagementQueryKeys } from './useDiaryManagementData';
import { meetingsQueryKeys } from './useMeetings';
import { getStartOfWeek } from '../../utils/dateHelpers';
import type { Meeting, FreeSlot } from '../../types/meetings';

interface PreloadProgress {
  calendarsLoaded: boolean;
  meetingsLoaded: boolean;
  error: string | null;
}

interface UseDiaryPreloadOptions {
  enabled?: boolean;
  onComplete?: () => void;
  onError?: (error: string) => void;
}

interface UseDiaryPreloadResult {
  isPreloading: boolean;
  isComplete: boolean;
  progress: PreloadProgress;
  startPreload: () => Promise<void>;
}

const getPreloadDateRange = (): { startDate: string; endDate: string } => {
  const today = new Date();
  const currentMonday = getStartOfWeek(today);
  
  const nextFriday = new Date(currentMonday);
  nextFriday.setDate(currentMonday.getDate() + 11);
  
  const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  return {
    startDate: formatDate(currentMonday),
    endDate: formatDate(nextFriday),
  };
};

const getDateRangeArray = (startDate: string, endDate: string): string[] => {
  const dates: string[] = [];
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  const current = new Date(start);
  while (current <= end) {
    const year = current.getFullYear();
    const month = String(current.getMonth() + 1).padStart(2, '0');
    const day = String(current.getDate()).padStart(2, '0');
    dates.push(`${year}-${month}-${day}`);
    current.setDate(current.getDate() + 1);
  }
  
  return dates;
};

/**
 * Hook to preload diary management data during the "Preparing your dashboard" screen.
 * 
 * Uses prefetchQuery for TanStack Query integration:
 * - If useMeetings requests the same data while preloading, it reuses the in-flight request
 * - No duplicate API calls
 * - Data is available instantly when user navigates to diary management
 */
const DIARY_PRELOAD_SESSION_KEY = 'diary.preloaded';

export const useDiaryPreload = ({
  enabled = true,
  onComplete,
  onError,
}: UseDiaryPreloadOptions = {}): UseDiaryPreloadResult => {
  const queryClient = useQueryClient();
  const [isPreloading, setIsPreloading] = useState(false);
  const [isComplete, setIsComplete] = useState(
    () => sessionStorage.getItem(DIARY_PRELOAD_SESSION_KEY) === '1'
  );
  const [progress, setProgress] = useState<PreloadProgress>({
    calendarsLoaded: false,
    meetingsLoaded: false,
    error: null,
  });

  const startPreload = useCallback(async () => {
    if (isPreloading || isComplete) return;
    
    setIsPreloading(true);
    setProgress({ calendarsLoaded: false, meetingsLoaded: false, error: null });
    
    try {
      // Step 1: Prefetch accessible calendars
      const calendarsData = await queryClient.fetchQuery({
        queryKey: diaryManagementQueryKeys.accessibleCalendars(),
        queryFn: () => diaryManagementService.getAccessibleCalendars(),
      });
      
      setProgress(prev => ({ ...prev, calendarsLoaded: true }));
      
      const calendarEmails = calendarsData.calendars.map(c => c.email);
      
      if (calendarEmails.length === 0) {
        setProgress(prev => ({ ...prev, meetingsLoaded: true }));
        setIsComplete(true);
        setIsPreloading(false);
        onComplete?.();
        return;
      }
      
      // Step 2: Prefetch 2 weeks of meetings for all calendars
      const { startDate, endDate } = getPreloadDateRange();
      
      // Build calendar name map
      const calendarNameMap = new Map<string, string>();
      calendarsData.calendars.forEach(cal => {
        calendarNameMap.set(cal.email, cal.name);
      });
      
      // Prefetch meetings for all calendars in parallel using prefetchQuery
      // This integrates with TanStack Query's request deduplication
      const prefetchPromises = calendarEmails.map(async (calendarEmail) => {
        try {
          await queryClient.prefetchQuery({
            queryKey: meetingsQueryKeys.calendarRange(calendarEmail, startDate, endDate),
            queryFn: async () => {
              const result = await meetingsService.getMeetings({
                calendar_emails: [calendarEmail],
                start_date: startDate,
                end_date: endDate,
              }, calendarNameMap);
              
              const dates = getDateRangeArray(startDate, endDate);
              for (const date of dates) {
                const dayKey = meetingsQueryKeys.calendarDay(calendarEmail, date);
                const dayMeetings = result.meetings.filter((m: Meeting) => {
                  const start = m.startDate instanceof Date ? m.startDate : new Date(m.startDate);
                  const end = m.endDate instanceof Date ? m.endDate : new Date(m.endDate);
                  const startDate_str = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`;
                  
                  // If meeting ends at midnight, it doesn't span into that day
                  const endAtMidnight = end.getHours() === 0 && end.getMinutes() === 0 && end.getSeconds() === 0;
                  const effectiveEnd = endAtMidnight ? new Date(end.getTime() - 1) : end;
                  const endDate_str = `${effectiveEnd.getFullYear()}-${String(effectiveEnd.getMonth() + 1).padStart(2, '0')}-${String(effectiveEnd.getDate()).padStart(2, '0')}`;
                  
                  return date >= startDate_str && date <= endDate_str;
                });
                
                queryClient.setQueryData(dayKey, {
                  meetings: dayMeetings,
                  freeSlots: result.freeSlots.filter((slot: FreeSlot) => {
                    const slotDateObj = slot.start instanceof Date ? slot.start : new Date(slot.start);
                    const sy = slotDateObj.getFullYear();
                    const smo = String(slotDateObj.getMonth() + 1).padStart(2, '0');
                    const sd = String(slotDateObj.getDate()).padStart(2, '0');
                    const slotDate = `${sy}-${smo}-${sd}`;
                    return slotDate === date;
                  }),
                  totalMeetings: dayMeetings.length,
                });
              }
              
              return result;
            },
          });

          return { calendarEmail, success: true };
        } catch (error) {
          console.warn(`Failed to preload meetings for calendar ${calendarEmail}:`, error);
          return { calendarEmail, success: false };
        }
      });
      
      await Promise.all(prefetchPromises);
      
      setProgress(prev => ({ ...prev, meetingsLoaded: true }));
      sessionStorage.setItem(DIARY_PRELOAD_SESSION_KEY, '1');
      setIsComplete(true);
      setIsPreloading(false);
      onComplete?.();
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to preload diary data';
      setProgress(prev => ({ ...prev, error: errorMessage }));
      setIsPreloading(false);
      onError?.(errorMessage);
      // Do not set the session key on error — allow retry on next page load
      setIsComplete(true);
    }
  }, [isPreloading, isComplete, queryClient, onComplete, onError]);

  useEffect(() => {
    if (enabled && !isPreloading && !isComplete) {
      startPreload();
    }
  }, [enabled, isPreloading, isComplete, startPreload]);

  return {
    isPreloading,
    isComplete,
    progress,
    startPreload,
  };
};
